import Decimal from 'decimal.js';
import { keyBy, partition } from 'lodash';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { AI_ONBOARDING_MODAL_VARIANT_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.constants';
import {
  AIOnboardingModalSuggestionKinds,
  AIOnboardingModalVariant,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { orderSuggestions } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.utils';
import { AISuggestionReviewCellDisplayItemProps } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewCell';
import { getEntityOptions } from '@/modules/entities/inputs/entityOptions';
import { EntityType } from '@/modules/entities/types/EntityType';
import { AiSuggestionKind } from '@/types/schema';
import { englishJoinStringList } from '@/utils/formatting/strings';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

import {
  AiOnboardingModal_EntityFragment,
  AiOnboardingModal_PossiblePrincipalClientProfileFragment,
} from '../aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '../aiSuggestions/aiSuggestions.contracts';
import {
  KGV2NodeForGrouping,
  resolvePrincipalsFromNodeMap,
} from '../aiSuggestions/kgV2Resolution.utils';

/**
 * Sorts the suggestions to review using the same ordering logic as the
 * AIOnboardingModal.
 *
 * @param suggestions
 * @param variant The AI onboarding modal variant to determine sorting logic
 */
export function orderSuggestionsToReview(
  suggestions: ReviewCardSuggestion[],
  variant?: AIOnboardingModalVariant,
  grantorIds?: string[]
): ReviewCardSuggestion[] {
  // For HOLDINGS variant, sort by descending monetary value
  if (variant === AIOnboardingModalVariant.HOLDINGS) {
    return [...suggestions].sort((a, b) => {
      // Sort Assets by assetValue (descending)
      if (a.asset && b.asset) {
        return new Decimal(b.asset.assetValue || '0').comparedTo(
          new Decimal(a.asset.assetValue || '0')
        );
      }

      // Sort Liabilities by outstandingBalance (descending)
      if (a.liability && b.liability) {
        return new Decimal(b.liability.outstandingBalance || '0').comparedTo(
          new Decimal(a.liability.outstandingBalance || '0')
        );
      }

      // Sort Life Insurance by deathBenefitAmount (descending)
      if (a.lifeInsurance && b.lifeInsurance) {
        return new Decimal(
          b.lifeInsurance.deathBenefitAmount || '0'
        ).comparedTo(new Decimal(a.lifeInsurance.deathBenefitAmount || '0'));
      }

      // If types don't match, maintain original order
      return 0;
    });
  }

  return orderSuggestions(suggestions, [], grantorIds);
}

/**
 * A suggestion narrowed to the kinds that the review card can render.
 * AiSuggestionForReviewCard also includes non-rendered reference suggestions.
 */
export type ReviewCardSuggestion = AiSuggestionForReviewCard & {
  kind: AIOnboardingModalSuggestionKinds;
};

/**
 * Returns the appropriate principal label based on entity type.
 *
 * - "Grantor(s)" for trusts (irrevocable, revocable, SLAT, GRAT, ILIT, QPRT)
 * - "Owner(s)" for business entities, accounts, etc.
 * - "Donor(s)" for charitable entities (CRT, CLT, DAF, private foundation)
 */
export function getEntityTypePrincipalLabel(
  entityType: EntityType | null | undefined
): string {
  switch (entityType) {
    case 'qprt':
    case 'grat':
    case 'slat':
    case 'ilit':
    case 'revocable-trust':
    case 'irrevocable-trust':
      return 'Grantor(s)';
    case 'individual-account':
    case 'joint-account':
    case 'retirement-account':
    case 'custodial-account':
    case 'qualified-tuition-account':
    case 'insurance-account':
    case 'sole-proprietorship':
    case 'c-corp':
    case 's-corp':
    case 'llc':
    case 'lp':
    case 'gp':
      return 'Owner(s)';
    case 'clt':
    case 'crt':
    case 'daf':
    case 'private-foundation':
      return 'Donor(s)';
    default:
      return 'Grantor(s)';
  }
}

/**
 * Returns the display label for an entity type.
 * If no matching label is found, returns the original entity type.
 *
 * @param entityType The entity type enum value
 * @returns The display label for the entity type
 */
export function getEntityTypeLabel(entityType: EntityType): string {
  if (!entityType) return '';

  const { trusts, businessEntities, charitableEntities, personalAccounts } =
    getEntityOptions();

  const allEntityOptions = [
    ...trusts,
    ...businessEntities,
    ...charitableEntities,
    ...personalAccounts,
  ];

  const entityOption = allEntityOptions.find(
    (option) => option.value === entityType
  );

  return entityOption?.label || entityType;
}

/**
 * Returns the display text for suggestion types based on the suggestion kinds.
 * Generates appropriate text like "entities", "individuals", "entities and individuals", etc.
 *
 * @param suggestionKinds Array of suggestion kinds to process
 * @returns Formatted display text for the suggestion types
 */
export function getSuggestionTypeDisplayText(
  suggestionKinds: AiSuggestionKind[]
): string {
  const hasEntities = suggestionKinds.some(
    (kind) =>
      kind === AiSuggestionKind.OnboardingEntity ||
      kind === AiSuggestionKind.OnboardingTestamentaryEntity
  );
  const hasIndividuals = suggestionKinds.includes(
    AiSuggestionKind.OnboardingIndividual
  );
  const hasOrganizations = suggestionKinds.includes(
    AiSuggestionKind.OnboardingOrganization
  );

  const types = [];
  if (hasEntities) types.push('entities');
  if (hasIndividuals) types.push('individuals');
  if (hasOrganizations) types.push('organizations');

  return englishJoinStringList(types) || 'data'; // fallback
}

/**
 * Processes a list of AI suggestions and returns the partitioned results for the review card.
 * This consolidates the common logic used in both the provider's useEffect and onCompleted callback.
 *
 * @param allSuggestions The raw list of suggestions to process
 * @param variant The AI onboarding modal variant to filter suggestions for
 * @returns Object containing suggestionsByID, reviewSuggestions, and otherSuggestions
 */
export function processSuggestionsForReviewCard(
  allSuggestions: AiSuggestionForReviewCard[],
  variant: AIOnboardingModalVariant
): {
  suggestionsByID: Record<string, AiSuggestionForReviewCard>;
  reviewSuggestions: ReviewCardSuggestion[];
  otherSuggestions: AiSuggestionForReviewCard[];
} {
  const variantSuggestionKinds =
    AI_ONBOARDING_MODAL_VARIANT_CONFIGS[variant].suggestionKinds;

  // Partition suggestions into those that match the variant kinds and are pending review,
  const [partitionReview, partitionOther] = partition(
    allSuggestions,
    (s): s is ReviewCardSuggestion =>
      variantSuggestionKinds.includes(
        s.kind as AIOnboardingModalSuggestionKinds
      ) && s.acceptanceStatus === null
  );

  const suggestionsByID = keyBy(allSuggestions, 'id');

  return {
    suggestionsByID,
    reviewSuggestions: partitionReview,
    otherSuggestions: partitionOther,
  };
}

export interface AssetValidationResult {
  nameMissing: boolean;
  categoryMissing: boolean;
  valueMissing: boolean;
  hasWarning: boolean;
}

export function validateAssetSuggestion(
  suggestion: AiSuggestionForReviewCard,
  assetCategoryOptions: SelectInputOption<string>[]
): AssetValidationResult {
  const s = suggestion.asset;
  if (!s)
    return {
      nameMissing: false,
      categoryMissing: false,
      valueMissing: false,
      hasWarning: false,
    };

  const nameText = s.assetTitle || '';
  const categoryDisplay = assetCategoryOptions.find(
    (o) => o.value === s.assetCategoryID
  )?.display;
  const valueText = s.assetValue ? s.assetValue : '';

  const nameMissing = !nameText;
  const categoryMissing = !categoryDisplay;
  const valueMissing = !valueText;

  return {
    nameMissing,
    categoryMissing,
    valueMissing,
    hasWarning: nameMissing || categoryMissing || valueMissing,
  };
}

export interface LiabilityValidationResult {
  nameMissing: boolean;
  balanceMissing: boolean;
  counterpartyNeedsWarning: boolean;
  hasWarning: boolean;
}

/**
 * Validates a liability suggestion and returns validation results.
 *
 * V2 liabilities use the explicit Entity edge as their debtor/owner.
 */
export function validateLiabilitySuggestion(
  suggestion: AiSuggestionForReviewCard,
  _entities: AiOnboardingModal_EntityFragment[]
): LiabilityValidationResult {
  const s = suggestion.liability;
  if (!s) {
    return {
      nameMissing: false,
      balanceMissing: false,
      counterpartyNeedsWarning: false,
      hasWarning: false,
    };
  }

  const nameMissing = !s.liabilityName;
  const balanceMissing = !s.outstandingBalance;
  if (suggestion.entity?.id) {
    return {
      nameMissing,
      balanceMissing,
      counterpartyNeedsWarning: false,
      hasWarning: nameMissing || balanceMissing,
    };
  }

  return {
    nameMissing,
    balanceMissing,
    counterpartyNeedsWarning: true,
    hasWarning: true,
  };
}

export interface IndividualValidationResult {
  hasRequiredNameFields: boolean;
  hasWarning: boolean;
}

export function validateIndividualSuggestion(
  suggestion: AiSuggestionForReviewCard
): IndividualValidationResult {
  const suggestionData = suggestion.onboardingIndividual;
  if (!suggestionData)
    return { hasRequiredNameFields: false, hasWarning: false };

  const hasRequiredNameFields = Boolean(
    suggestionData.firstName && suggestionData.lastName
  );

  return {
    hasRequiredNameFields,
    hasWarning: !hasRequiredNameFields,
  };
}

export interface OrganizationValidationResult {
  hasRequiredNameField: boolean;
  hasRequiredTypeField: boolean;
  hasWarning: boolean;
}

export function validateOrganizationSuggestion(
  suggestion: AiSuggestionForReviewCard
): OrganizationValidationResult {
  const suggestionData = suggestion.onboardingOrganization;
  if (!suggestionData)
    return {
      hasRequiredNameField: false,
      hasRequiredTypeField: false,
      hasWarning: false,
    };

  const hasRequiredNameField = Boolean(suggestionData.organizationName);
  const hasRequiredTypeField = Boolean(suggestionData.organizationKind);

  return {
    hasRequiredNameField,
    hasRequiredTypeField,
    hasWarning: !hasRequiredNameField || !hasRequiredTypeField,
  };
}

export interface LifeInsuranceValidationResult {
  policyTypeMissing: boolean;
  deathBenefitMissing: boolean;
  insuredMissing: boolean;
  hasWarning: boolean;
}

export function validateLifeInsuranceSuggestion(
  suggestion: AiSuggestionForReviewCard,
  possibleGrantors: AiOnboardingModal_PossiblePrincipalClientProfileFragment[]
): LifeInsuranceValidationResult {
  const s = suggestion.lifeInsurance;
  if (!s)
    return {
      policyTypeMissing: false,
      deathBenefitMissing: false,
      insuredMissing: false,
      hasWarning: false,
    };

  const policyTypeText = s.policyType ? s.policyType : '';
  const deathBenefitText = s.deathBenefitAmount ? s.deathBenefitAmount : '';

  const insuredClientProfiles = s.insuredClientProfiles || [];
  const insuredNames = insuredClientProfiles
    .map((profile) => {
      const client = possibleGrantors?.find(
        (g) => g.id === profile.clientProfileID
      );
      return client?.displayName;
    })
    .filter(Boolean);
  const insuredText = insuredNames.join(', ');
  const insuredMissing = !insuredText;

  const policyTypeMissing = !policyTypeText;
  const deathBenefitMissing = !deathBenefitText;

  return {
    policyTypeMissing,
    deathBenefitMissing,
    insuredMissing,
    hasWarning: policyTypeMissing || insuredMissing || deathBenefitMissing,
  };
}

export interface TestamentaryEntityValidationResult {
  assetLocationMissing: boolean;
  hasWarning: boolean;
}

/**
 * Validates a testamentary entity suggestion and returns validation results.
 * For two-client households, asset location is required. For single-client households,
 * asset location defaults to OUT_OF_ESTATE, so no validation is needed.
 */
export function validateTestamentaryEntitySuggestion(
  suggestion: AiSuggestionForReviewCard,
  isTwoClientHousehold: boolean
): TestamentaryEntityValidationResult {
  const s = suggestion.onboardingTestamentaryEntity;
  if (!s) {
    return {
      assetLocationMissing: false,
      hasWarning: false,
    };
  }

  // For single-client households, asset location defaults to OUT_OF_ESTATE, so no warning needed
  if (!isTwoClientHousehold) {
    return {
      assetLocationMissing: false,
      hasWarning: false,
    };
  }

  // For two-client households, asset location is required
  const assetLocationMissing = !s.inEstateStatus;

  return {
    assetLocationMissing,
    hasWarning: assetLocationMissing,
  };
}

export interface EntityValidationResult {
  principalCellValues: AISuggestionReviewCellDisplayItemProps[];
  hasWarning: boolean;
}

/**
 * Validates an entity suggestion's principals (grantors/owners/donors) by
 * resolving its `principalNodeIDs` against the household's KG V2 node map
 * (see `resolvePrincipalsFromNodeMap`).
 *
 * The suggestion warns when it has no principals at all, or when any principal
 * has not yet been created as a person/org/entity (its node doesn't resolve to
 * a data-model record).
 */
export function validateEntitySuggestion(
  suggestion: AiSuggestionForReviewCard,
  principalNodesById: Map<string, KGV2NodeForGrouping>
): EntityValidationResult {
  const suggestionData = suggestion.onboardingEntity;
  if (!suggestionData) return { principalCellValues: [], hasWarning: false };

  const resolvedPrincipals = resolvePrincipalsFromNodeMap(
    suggestionData.principalNodeIDs ?? [],
    principalNodesById
  );

  const principalCellValues: AISuggestionReviewCellDisplayItemProps[] =
    resolvedPrincipals.map((principal) => ({
      displayText: principal.isResolved
        ? principal.resolvedDisplayName || principal.nodeName
        : sentenceCaseWithAcronyms(principal.nodeName) || 'Unknown',
      showWarning: !principal.isResolved,
      warningTooltip: principal.isResolved
        ? undefined
        : 'Individual or entity not yet created. Review required.',
    }));

  return {
    principalCellValues,
    hasWarning:
      principalCellValues.length === 0 ||
      principalCellValues.some((value) => value.showWarning),
  };
}

export interface SuggestionWarningContext {
  possibleGrantors: AiOnboardingModal_PossiblePrincipalClientProfileFragment[];
  entities: AiOnboardingModal_EntityFragment[];
  assetCategoryOptions: SelectInputOption<string>[];
  isTwoClientHousehold: boolean;
  /**
   * KG V2 nodes referenced by the entity suggestions' `principalNodeIDs`,
   * keyed by node ID. Used to resolve each principal to its data-model record
   * (see `validateEntitySuggestion`).
   */
  principalNodesById: Map<string, KGV2NodeForGrouping>;
}

/**
 * Checks if a suggestion has validation warnings that would prevent immediate creation.
 * This function mirrors the warning logic from each row component to determine
 * whether a suggestion can be created without review.
 *
 * @param suggestion The AI suggestion to check
 * @param context Context data needed for warning checks
 * @returns True if the suggestion has warnings, false otherwise
 */
export function checkSuggestionHasWarning(
  suggestion: AiSuggestionForReviewCard,
  context: SuggestionWarningContext
): boolean {
  switch (suggestion.kind) {
    case AiSuggestionKind.OnboardingEntity:
      return validateEntitySuggestion(suggestion, context.principalNodesById)
        .hasWarning;

    case AiSuggestionKind.Asset:
      return validateAssetSuggestion(suggestion, context.assetCategoryOptions)
        .hasWarning;

    case AiSuggestionKind.OnboardingIndividual:
      return validateIndividualSuggestion(suggestion).hasWarning;

    case AiSuggestionKind.Liability:
      return validateLiabilitySuggestion(suggestion, context.entities)
        .hasWarning;

    case AiSuggestionKind.LifeInsurance:
      return validateLifeInsuranceSuggestion(
        suggestion,
        context.possibleGrantors
      ).hasWarning;

    case AiSuggestionKind.OnboardingOrganization:
      return validateOrganizationSuggestion(suggestion).hasWarning;

    case AiSuggestionKind.OnboardingTestamentaryEntity:
      return validateTestamentaryEntitySuggestion(
        suggestion,
        context.isTwoClientHousehold
      ).hasWarning;

    default:
      return false;
  }
}

/**
 * Returns the suggestions that can be created without additional review.
 */
export function getCreatableSuggestions(
  suggestions: AiSuggestionForReviewCard[],
  context: SuggestionWarningContext
): AiSuggestionForReviewCard[] {
  return suggestions.filter(
    (suggestion) => !checkSuggestionHasWarning(suggestion, context)
  );
}
